import type { StoreApi } from "zustand";
import type { TChatStore } from "../chat-store.types";
import type { TPaginationKey } from "../chat-domain.types";
import { USER_TAG } from "@/utils/constants";

type TSetState = StoreApi<TChatStore>["setState"];

export const createConversationActions = (set: TSetState) => ({
  setConversationActive: (active: TChatStore["conversation"]["active"]) =>
    set((state) => ({ conversation: { ...state.conversation, active } })),

  resetConversationActive: () =>
    set((state) => ({
      conversation: { ...state.conversation, active: undefined },
    })),

  setConversationChatType: (chatType: string | undefined) =>
    set((state) => ({
      conversation: { ...state.conversation, chatType, active: undefined },
    })),

  // Contador das badges das abas vindo da action dedicada getUnreadCount
  // (total real por perspectiva), aplicado de forma autoritativa — mesmo padrão do
  // Header. O efeito instantâneo no clique vem do removeUnreadChats (otimista); este
  // reconcilia com o total fresco do back. Ver ANCR-5329.
  setUnreadChatsCount: ({ from, to }: { from: number; to: number }) =>
    set((state) => ({
      conversation: {
        ...state.conversation,
        unreadChats: { from, to },
      },
    })),

  removeUnreadChats: () =>
    set((state) => {
      const chatType = state.conversation.chatType === "from" ? "from" as const : "to" as const;
      return {
        conversation: {
          ...state.conversation,
          unreadChats: {
            ...state.conversation.unreadChats,
            [chatType]: state.conversation.unreadChats[chatType] - 1,
          },
        },
      };
    }),

  setConversationStatus: ({
    chatType,
    status,
    hasOlderChats,
    empty,
    unreadChats,
  }: {
    chatType: "from" | "to";
    status: string;
    hasOlderChats: TPaginationKey;
    empty: boolean;
    unreadChats: number;
  }) =>
    set((state) => ({
      conversation: {
        ...state.conversation,
        status: { ...state.conversation.status, [chatType]: status },
        hasOlderChats: {
          ...state.conversation.hasOlderChats,
          [chatType]: hasOlderChats,
        },
        empty: { ...state.conversation.empty, [chatType]: empty },
        unreadChats: {
          ...state.conversation.unreadChats,
          [chatType]: unreadChats,
        },
      },
    })),

  setSelectedTags: (tags: string[]) =>
    set((state) => ({
      conversation: { ...state.conversation, selectedTags: tags },
    })),

  blockUserConversation: (payload: { chatId: string; blocked: boolean }) =>
    set((state) => {
      if (state.conversation.active?.chatId === payload?.chatId) {
        return {
          conversation: {
            ...state.conversation,
            active: {
              ...state.conversation.active,
              userTag: payload.blocked ? USER_TAG.Blocked : USER_TAG.NaturalPerson,
            },
          },
        };
      }
      return {};
    }),
});
